import {
  useLoadingState,
  useSiteContext,
  useSiteTitle,
  useTranslations
} from '@dustin-web/microsite-components/src/hooks'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks/use-graphql'
import Head from 'next/head'
import React from 'react'
import {
  GetCategoryForLandingPageQuery,
  GetCategoryForLandingPageQueryVariables,
} from '@productsite/src/graphql/generated/graphql-types'
import { GetCategoryForLandingPage } from '@productsite/src/graphql/get-category-for-landing-page.graphql'
import { CategoryPage, useCategoryPageMetadata } from '.'
import { CategorySchema } from '../schemas/collection-page-schema'
import { stripTrailingSlash } from '@dustin-web/microsite-components/src/utils/urls'

type CategoryLandingPageProps = {
  categoryId: string
  initialData?: GetCategoryForLandingPageQuery | null
}

const CategoryPageLayout = ({ categoryId, initialData }: CategoryLandingPageProps) => {
  const { isLoading } = useLoadingState()

  const { data, error, isValidating } = useGraphql<
    GetCategoryForLandingPageQuery,
    GetCategoryForLandingPageQueryVariables
  >(GetCategoryForLandingPage, {
    variables: {
      categoryId,
    },
    swrOptions: {
      ...(initialData ? { fallbackData: initialData } : {}),
    },
  })

  const loading = isLoading || isValidating

  if (loading && !data) {
    return null
  }

  if (error) {
    throw error
  }

  const category = data?.category

  if (!category) {
    return (
      <>
        <Meta data={data} category={category} />
        <div className="container">
          <div className="col-12 u-py-32">
            <NotFoundText />
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Meta data={data} category={category} />
      <CategorySchema page={data} />
      <CategoryPage categoryId={categoryId} category={category} loading={loading} />
    </>
  )
}

const NotFoundText = () => {
  const { translate } = useTranslations()
  return <p>{translate('NoProductsFound')}</p>
}

const Meta = ({
  data,
  category,
}: {
  data: GetCategoryForLandingPageQuery | undefined
  category: GetCategoryForLandingPageQuery['category']
}) => {
  const { title, metaDescription } = useCategoryPageMetadata(
    data?.category?.seoTitle ?? null,
    data?.category?.seoMetaDescription ?? null,
    data?.category?.seoLongTitleName ?? data?.category?.name ?? '',
    data?.category?.navigation?.path ?? []
  )
  const { siteUrl } = useSiteContext()
  const { getSiteTitle } = useSiteTitle()

  if (!category) {
    return null
  }

  const canonicalUrl = `${siteUrl}/group/${stripTrailingSlash(category?.pathSlug)}`
  
  return (
    <Head>
      <title>{getSiteTitle(title)}</title>
      {metaDescription && <meta name="description" content={metaDescription} />}
      <link rel="canonical" href={canonicalUrl} />
    </Head>
  )
}
export default CategoryPageLayout
